import type { User } from 'oidc-client-ts';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
    getUserManager,
    renewSession,
    startAccountSwitchRedirect,
    startLoginRedirect,
    startLogoutRedirect,
} from '~/lib/oidc';
import AuthSessionContext, { AuthSession } from './AuthSessionContext';

interface Props {
    /**
     * The session resolved during bootstrap — either redeemed from a login
     * redirect or restored from storage. Resolving before the first render
     * keeps the route guard from treating a not-yet-loaded session as
     * signed out and bouncing to the login page.
     */
    initialUser: User | null;
    children: ReactNode;
}

/** Display name, falling back through the claims a broker may omit. */
const resolveUserName = (user: User | null): string | undefined => {
    const profile = user?.profile;
    if (profile === undefined) {
        return undefined;
    }
    return profile.name ?? profile.preferred_username ?? profile.email;
};

/**
 * Publishes the identity-broker session to the application.
 *
 * The user is re-read on every UserManager event rather than held from
 * bootstrap alone, since renewal replaces the stored user.
 *
 * The session is renewed shortly before its access token expires. A renewal
 * that fails then leaves the still-valid token in place, and the session ends
 * only when the token has expired and the broker refuses a further renewal:
 * ending it sends the user through a full-page sign-in, which a passing
 * network fault must not cost them. A renewal the broker never answers keeps
 * the session even past expiry, and the next token read renews it.
 */
const BrokerAuthSessionProvider: React.FC<Props> = ({
    initialUser,
    children,
}) => {
    const [user, setUser] = useState<User | null>(initialUser);

    useEffect(() => {
        const manager = getUserManager();
        const handleUserLoaded = (loaded: User) => setUser(loaded);
        const handleUserSignedOut = () => setUser(null);
        // A renewal the broker never answered is already logged, and changes
        // nothing here: the session is kept, and the next token read renews
        // it.
        const handleTokenExpiring = () =>
            void renewSession().catch(() => undefined);
        const handleTokenExpired = () =>
            void renewSession().then(
                (renewed) => {
                    if (renewed === null) {
                        setUser(null);
                    }
                },
                () => undefined
            );

        manager.events.addUserLoaded(handleUserLoaded);
        manager.events.addUserUnloaded(handleUserSignedOut);
        manager.events.addAccessTokenExpiring(handleTokenExpiring);
        manager.events.addAccessTokenExpired(handleTokenExpired);

        return () => {
            manager.events.removeUserLoaded(handleUserLoaded);
            manager.events.removeUserUnloaded(handleUserSignedOut);
            manager.events.removeAccessTokenExpiring(handleTokenExpiring);
            manager.events.removeAccessTokenExpired(handleTokenExpired);
        };
    }, []);

    const session = useMemo<AuthSession>(
        () => ({
            isAuthenticated: user !== null && !user.expired,
            // Settled during bootstrap, before this provider mounts.
            isPending: false,
            userName: resolveUserName(user),
            userSignInName:
                user?.profile.email ?? user?.profile.preferred_username,
            logIn: startLoginRedirect,
            switchAccount: startAccountSwitchRedirect,
            logOut: startLogoutRedirect,
        }),
        [user]
    );

    return (
        <AuthSessionContext.Provider value={session}>
            {children}
        </AuthSessionContext.Provider>
    );
};

export default BrokerAuthSessionProvider;
